import React, { type ComponentType, type FunctionComponent } from 'react';
import { withIpaPageContext, type IpaHandler } from '../contexts/IpaPageContext';

// Registry of page components that can be loaded from user config
// The key should match the `pageComponent` path in user config (e.g., "home/HomepageView")

type PageComponentProps = {
  config?: Record<string, unknown>;
  handler?: IpaHandler;
  selectedItems?: Record<string, unknown>;
  onLoadComplete?: () => void;
};

type PageComponent = ComponentType<PageComponentProps> | FunctionComponent<PageComponentProps>;

// Eager loaded components (always available)
import { HomepageView } from './Home/HomepageView';
import { OrchestratorManagementPage } from './OrchestratorManagement';
import { WorkflowManagementPage } from './WorkflowManagement';
import { NotificationManagementPage } from './NotificationManagement';
import { InviteTemplateManagementPage } from './InviteTemplateManagement';
import { OnboardingSettingsPage } from './OnboardingSettings';
import DevUserGroupView from './users/DevUserGroupView';
import CreateSandboxView from "./sandbox/CreateSandboxView";
import SandboxView from "./sandbox/SandboxView";
import DeveloperOnboardingView from "./onboarding/DeveloperOnboardingView";
import IpaPageComponents from "@invicara/ipa-core/esm_modules/IpaPageComponents"
const InternalPages = IpaPageComponents?.InternalPages;
/**
 * Helper to wrap InternalPages components with proper handler props
 * InternalPages components expect: { handler: { config: any } }
 * This function normalizes props and wraps with IpaPageContext
 */
function wrapInternalPageComponent(
  pageComponentPath: string,
  fallbackConfig?: Record<string, unknown>
): PageComponent {
  return (props: PageComponentProps) => {
    let Component = InternalPages?.[pageComponentPath];

    if (!Component) {
      console.error(`Component not found in InternalPages: ${pageComponentPath}`);
      return React.createElement('div', { style: { padding: '20px' } },
        `Component "${pageComponentPath}" is not available`
      );
    }

    // InternalPages can contain either React components directly, or objects wrapping them
    // Check if it's an object with $$typeof (React component) or a plain object that needs unwrapping
    if (typeof Component === 'object' && !('$$typeof' in Component)) {
      // It's a plain object, try to find the actual component inside
      const possibleComponent = (Component as any)[pageComponentPath.split('/').pop() || ''] ||
                                (Component as any).default ||
                                (Component as any).component;
      if (possibleComponent) {
        Component = possibleComponent;
      }
    }

    // Validate we have a valid React component
    if (!Component || (typeof Component !== 'function' && !('$$typeof' in (Component as any)))) {
      console.error(`Component is not a valid React component: ${pageComponentPath}`, Component);
      return React.createElement('div', { style: { padding: '20px' } },
        `Component "${pageComponentPath}" is not valid`
      );
    }

    // Build handler object expected by ipa-core components
    const handler: IpaHandler = props.handler || {
      config: props.config || fallbackConfig || {}
    };

    // Wrap with IpaPageContext and pass handler prop
    const WrappedComponent = withIpaPageContext(Component as any, handler);
    return React.createElement(WrappedComponent, props);
  };
}

/**
 * Wrap a local component (not from InternalPages) with IpaPageContext
 * This ensures local components also get user, userConfig, actions, etc.
 */
function wrapLocalComponent(Component: any): PageComponent {
  return (props: PageComponentProps) => {
    const handler: IpaHandler = props.handler || { config: props.config || {} };
    const WrappedComponent = withIpaPageContext(Component, handler);
    return React.createElement(WrappedComponent, props);
  };
}

// Component registry - maps pageComponent paths to actual components
const componentRegistry: Record<string, PageComponent> = {
  // Home pages
  'home/HomepageView': HomepageView,

  // Management pages
  'orchestrator/OrchestratorManagementPage': OrchestratorManagementPage,
  'workflow/WorkflowManagementPage': WorkflowManagementPage,
  'notification/NotificationManagementPage': NotificationManagementPage,
  'invite-templates/InviteTemplateManagementPage': InviteTemplateManagementPage,
  'onboarding-settings/OnboardingSettingsPage': OnboardingSettingsPage,
  'onboarding/DeveloperOnboardingView': wrapLocalComponent(DeveloperOnboardingView),

  // User management (wrapped to receive context)
  'users/DevUserGroupView': wrapLocalComponent(DevUserGroupView),

  // Sandbox pages (wrapped to receive context)
  'sandbox/SandboxView': wrapLocalComponent(SandboxView),
  'sandbox/CreateSandboxView': wrapLocalComponent(CreateSandboxView),

  'dashboards/DashboardView': wrapInternalPageComponent('dashboards/DashboardView'),
  'users/UserGroupView': wrapInternalPageComponent('users/UserGroupView'),
  //'modelImport/SimpleModelImportView': SimpleModelImportView,
};

// ============================================================================
// BUILT-IN PAGES
// These pages are always available in the app, regardless of user config
// ============================================================================

export interface BuiltInPage {
  key: string;
  path: string;
  title: string;
  icon: string;
  Component: PageComponent;
  group?: string; // Optional group name for sidebar organization
}

export const builtInPages: BuiltInPage[] = [
  /*{
    key: 'onboarding-management',
    path: '/onboarding/DeveloperOnboardingView',
    title: 'Onboarding Management',
    icon: 'fas fa-clock',
    Component: componentRegistry['onboarding/DeveloperOnboardingView'],
    group: 'Admin Tools',
  },
  {
    key: 'workflow-management',
    path: '/admin/workflows',
    title: 'Workflow Management',
    icon: 'fas fa-project-diagram',
    Component: WorkflowManagementPage,
    group: 'Admin Tools',
  }
  {
    key: 'notification-management',
    path: '/admin/notifications',
    title: 'Notification Management',
    icon: 'fas fa-bell',
    Component: NotificationManagementPage,
    group: 'Admin Tools',
  },*/
  {
    key: 'invite-template-management',
    path: '/admin/invite-templates',
    title: 'Invite Email Templates',
    icon: 'fas fa-envelope-open-text',
    Component: InviteTemplateManagementPage,
    group: 'Admin Tools',
  },
  {
    key: 'onboarding-settings',
    path: '/admin/onboarding-settings',
    title: 'App Invite Templates',
    icon: 'fas fa-cogs',
    Component: OnboardingSettingsPage,
    group: 'Admin Tools',
  },
  // Add more built-in pages here as needed
];

/**
 * Get a page component by its path from user config
 * @param pageComponentPath - The path from user config (e.g., "home/HomepageView")
 * @returns The component or null if not found
 */
export function getPageComponent(pageComponentPath: string): PageComponent | null {
  // Try exact match first
  if (componentRegistry[pageComponentPath]) {
    return componentRegistry[pageComponentPath];
  }

  // Try without leading path segments (e.g., "HomepageView" for "home/HomepageView")
  const fileName = pageComponentPath.split('/').pop();
  if (fileName) {
    const match = Object.entries(componentRegistry).find(([key]) =>
      key.endsWith('/' + fileName) || key === fileName
    );
    if (match) {
      return match[1];
    }
  }

  return null;
}

/**
 * Check if a page component is registered
 */
export function hasPageComponent(pageComponentPath: string): boolean {
  return getPageComponent(pageComponentPath) !== null;
}

/**
 * Get all registered component paths
 */
export function getRegisteredComponents(): string[] {
  return Object.keys(componentRegistry);
}

export default componentRegistry;


